import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Handshake, LogIn, UserPlus, Building2 } from 'lucide-react';

function PartnerLogin() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Signup form
  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    phone: '',
    country: '',
  });
  const [signupError, setSignupError] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data.role === 'partner') {
        navigate('/partner/dashboard');
      }
    } catch (error) {
      // Not logged in, stay on login page
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    
    try {
      localStorage.removeItem('staff_session_token');
      localStorage.removeItem('staff_type');
      delete axios.defaults.headers.common['Authorization'];

      const response = await axios.post(`${API}/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      }, { withCredentials: true });
      
      if (response.data.role !== 'partner') {
        setLoginError('This login is only for partners');
        return;
      }

      if (response.data.session_token) {
        document.cookie = `session_token=${response.data.session_token}; path=/; SameSite=Lax; Max-Age=${30 * 24 * 3600}`;
      }
      
      toast.success('Login successful');
      navigate('/partner/dashboard', {
        state: {
          user: {
            user_id: response.data.user_id,
            email: response.data.email,
            name: response.data.name,
            role: response.data.role,
            session_token: response.data.session_token
          }
        }
      });
    } catch (error) {
      setLoginError(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError('');
    setLoading(true);
    
    if (!signupData.name || !signupData.email || !signupData.password) {
      setSignupError('Name, email, and password are required');
      setLoading(false);
      return;
    }
    
    if (signupData.password.length < 6) {
      setSignupError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }
    
    try {
      await axios.post(`${API}/partner/signup`, signupData);
      toast.success('Registration successful! You can now login.');
      
      // Auto login after signup
      const loginRes = await axios.post(`${API}/auth/login`, {
        email: signupData.email,
        password: signupData.password,
      }, { withCredentials: true });
      
      if (loginRes.data.role === 'partner') {
        navigate('/partner/dashboard');
      }
    } catch (error) {
      setSignupError(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-12 object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <Handshake className="w-10 h-10 text-purple-600" />
                <span className="text-2xl font-bold text-purple-600">Partner Portal</span>
              </div>
            )}
          </div>
          <CardTitle className="text-xl">Partner Login</CardTitle>
          <CardDescription>
            Access your partner dashboard and manage referrals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </TabsTrigger>
              <TabsTrigger value="signup">
                <UserPlus className="w-4 h-4 mr-2" />
                Sign Up
              </TabsTrigger>
            </TabsList>
            
            {/* Login Tab */}
            <TabsContent value="login" className="mt-4">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="partner@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Your password"
                    required
                  />
                </div>
                {loginError && (
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{loginError}</p>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            {/* Signup Tab */}
            <TabsContent value="signup" className="mt-4">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Name *</Label>
                    <Input
                      id="signup-name"
                      value={signupData.name}
                      onChange={(e) => setSignupData({...signupData, name: e.target.value})}
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email *</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={signupData.email}
                      onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                      placeholder="partner@example.com"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password *</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={signupData.password}
                    onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                    placeholder="Min 6 characters"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-company">Company Name</Label>
                    <Input
                      id="signup-company"
                      value={signupData.company_name}
                      onChange={(e) => setSignupData({...signupData, company_name: e.target.value})}
                      placeholder="Your company"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-phone">Phone</Label>
                    <Input
                      id="signup-phone"
                      value={signupData.phone}
                      onChange={(e) => setSignupData({...signupData, phone: e.target.value})}
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-country">Country</Label>
                  <Input
                    id="signup-country"
                    value={signupData.country}
                    onChange={(e) => setSignupData({...signupData, country: e.target.value})}
                    placeholder="Your country"
                  />
                </div>
                {signupError && (
                  <p className="text-sm text-red-600 bg-red-50 p-2 rounded">{signupError}</p>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Creating account...' : 'Create Partner Account'}
                </Button>
                <p className="text-xs text-center text-gray-500">
                  Default profit share is 20% on all referred company payments
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

export default PartnerLogin;
